import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { getServerErrorMessage } from "Utils";
import { ChildrenProps } from "Utils/types";
import { getCurrentSemanticMapId, setShowGlobalLoader } from "Slices/appState";
import { fetchStation } from "Slices/operatorStation";
import { fetchMissionAssets } from "Slices/operatorMissionAssets";
import { fetchGetFleets, getIsFleetsLoaded } from "Slices/fleets";
import { getIsFleetsLoadingError } from "Slices/fleets/selectors";
import {
  fetchGetStationVehiclesFromCurrentSemanticMap,
  getIsStationVehiclesLoaded,
  fetchGetMissionsInQueueFromCurrentSemanticMap,
  getIsMissionsInQueueLoaded,
} from "Slices/stationConfirmation";
import { setErrorWarning } from "Slices/warning";
import {
  fetchGetVehicles,
  getIsVehiclesLoaded,
  getIsVehiclesLoadingError,
} from "Slices/vehicles";
import {
  fetchGetVehicleTypes,
  getIsVehicleTypesLoaded,
} from "Slices/vehicleTypes";
import { useStopsLoader } from "Utils/loaders";
import { useVdaActionDefinitionsLoader } from "Utils/loaders";

const useHandleDataLoadingErrors = () => {
  const dispatch = useDispatch();
  const isFleetsLoadingError = useSelector(getIsFleetsLoadingError);
  const isVehiclesLoadingError = useSelector(getIsVehiclesLoadingError);

  useEffect(() => {
    if (isFleetsLoadingError || isVehiclesLoadingError) {
      const problemData = `
       ${isFleetsLoadingError ? "FLEETS" : ""}
       ${isVehiclesLoadingError ? "VEHICLES" : ""}
      `.trim();

      dispatch(setShowGlobalLoader(false));
      dispatch(setErrorWarning(getServerErrorMessage(problemData)));
    }
  }, [isFleetsLoadingError, isVehiclesLoadingError]);
};

export const StationsLoader = ({ children }: ChildrenProps) => {
  const dispatch = useDispatch();
  const semanticMapId = useSelector(getCurrentSemanticMapId);
  const isFleetsLoaded = useSelector(getIsFleetsLoaded);
  const currentSemanticMapId = useSelector(getCurrentSemanticMapId);
  const isVehiclesLoaded = useSelector(getIsVehiclesLoaded);
  const isVehicleTypesLoaded = useSelector(getIsVehicleTypesLoaded);
  const isStationVehiclesLoaded = useSelector(getIsStationVehiclesLoaded);
  const isMissionsInQueueLoaded = useSelector(getIsMissionsInQueueLoaded);

  useHandleDataLoadingErrors();

  const { isStopsLoaded, isStopsLoadingError } =
    useStopsLoader(currentSemanticMapId);
  const { isVdaActionDefinitionsLoaded, isVdaActionDefinitionsLoadingError } =
    useVdaActionDefinitionsLoader();

  useEffect(() => {
    if (!isStopsLoadingError) return;
    dispatch(setShowGlobalLoader(false));
    dispatch(setErrorWarning(getServerErrorMessage("STOPS")));
  }, [isStopsLoadingError]);

  useEffect(() => {
    if (!isVdaActionDefinitionsLoadingError) return;
    dispatch(setShowGlobalLoader(false));
    dispatch(setErrorWarning(getServerErrorMessage("VDA ACTION DEFINITIONS")));
  }, [isVdaActionDefinitionsLoadingError]);

  // stops loading
  useEffect(() => {
    if (isStopsLoaded) return;
    dispatch(setShowGlobalLoader(true));
  }, [isStopsLoaded]);

  // vda action definitions loading
  useEffect(() => {
    if (isVdaActionDefinitionsLoaded) return;
    dispatch(setShowGlobalLoader(true));
  }, [isVdaActionDefinitionsLoaded]);

  // GET /user/stations?semantic_map
  useEffect(() => {
    if (!semanticMapId) return;

    dispatch(fetchStation());
  }, [semanticMapId]);

  // GET /operatorMissionAssets
  useEffect(() => {
    if (!semanticMapId) return;

    dispatch(fetchMissionAssets());
  }, [semanticMapId]);

  // GET /vehicles
  useEffect(() => {
    if (isVehiclesLoaded) return;
    dispatch(setShowGlobalLoader(true));
    dispatch(fetchGetVehicles());
  }, [isVehiclesLoaded]);

  // GET /vehicleTypes
  useEffect(() => {
    if (isVehicleTypesLoaded) return;
    dispatch(setShowGlobalLoader(true));
    dispatch(fetchGetVehicleTypes());
  }, [isVehicleTypesLoaded]);

  // GET /fleets
  useEffect(() => {
    if (isFleetsLoaded) return;
    dispatch(setShowGlobalLoader(true));
    dispatch(fetchGetFleets());
  }, [isFleetsLoaded]);

  // Get vehicles waiting at the station from current semantic map
  useEffect(() => {
    if (semanticMapId) {
      dispatch(
        fetchGetStationVehiclesFromCurrentSemanticMap({ semanticMapId })
      );
    }
  }, [semanticMapId]);

  // Get missions in queue from current semantic map
  useEffect(() => {
    if (semanticMapId) {
      dispatch(
        fetchGetMissionsInQueueFromCurrentSemanticMap({ semanticMapId })
      );
    }
  }, [semanticMapId]);

  // turn the loader off
  useEffect(() => {
    if (
      isFleetsLoaded &&
      isStopsLoaded &&
      isVehiclesLoaded &&
      isVehicleTypesLoaded &&
      isStationVehiclesLoaded &&
      isMissionsInQueueLoaded
    ) {
      dispatch(setShowGlobalLoader(false));
    }
  }, [
    isFleetsLoaded,
    isStopsLoaded,
    isVehiclesLoaded,
    isVehicleTypesLoaded,
    isStationVehiclesLoaded,
    isMissionsInQueueLoaded,
  ]);

  return <>{children}</>;
};
